import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Button, Select, MenuItem, FormControl, InputLabel, Alert, Autocomplete, TextField } from '@mui/material';
import { AppConfig } from "../../../config/appConfig";
import { Buffer } from "buffer";

import { IMaterialPriceSearchRequest, IMaterialPriceSearchResult, IMaterialPriceSearchValueSets } from "../../../types/material";
import { ISelectOption } from "../../../types/list";

import { useUserContext } from "../../../contexts/userContext";

import { MaterialPriceService } from "../../../services/materialPriceService";

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import MaterialPriceTable from "../../tables/materialPriceTable";
import WorkingModal, { IWorkingModalProps } from '../../workingModal';

import "../../../styles/entity/materialPriceList.scss";

const MaterialPriceList = () => {
	const userContext = useUserContext();
	const [searchResults, setSearchResults] = useState<IMaterialPriceSearchResult>();

	//Filtering
	const [materialOptions, setMaterialOptions] = useState<ISelectOption[]>([]);
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [statusOptions] = useState<string[]>(["Active", "Inactive"]);
	const [materialFilter, setMaterialFilter] = useState('');
	const [customerFilter, setCustomerFilter] = useState('');
	const [vendorFilter, setVendorFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [previousSearchRequest, setPreviousSearchRequest] = useState<IMaterialPriceSearchRequest>();

	const [isLoadingMaterialPrices, setIsLoadingMaterialPrices] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const loadMaterialPriceData = async () => {
		setErrorMessage(undefined);

		try {
			await loadValueSets();

			const initialSearchRequest: IMaterialPriceSearchRequest = {
				pageIndex: 0,
				pageSize: AppConfig.materialPriceListDefaultPageSize,
				customer: '',
				material: '',
				vendor: '',
				status: '',
				sortField: AppConfig.materialPriceListDefaultSortField,
				sortAscending: true
			}
			await searchMaterialPrices(initialSearchRequest);
		}
		catch (error) {
			console.log('MaterialPriceService.loadMaterialPriceData: ERROR', error);
			if (error?.code == "ERR_NETWORK") {
				setErrorMessage(AppConfig.apiConnectionErrorMessage)
			}
		}
	};

	const loadValueSets = async () => {
		MaterialPriceService.getMaterialPriceSearchValueSets()
			.then((response) => {
				console.log('MaterialPriceService.getMaterialPriceSearchValueSets: SUCCESS', response);
				var valueSets = response.data as IMaterialPriceSearchValueSets;

				setMaterialOptions(valueSets.materials);
				setVendorOptions(valueSets.vendors);
				setCustomerOptions(valueSets.customers);
			})
			.catch((error) => {
				console.log('MaterialPriceService.getMaterialPriceSearchValueSets: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			});
	};

	const searchMaterialPrices = async (searchRequest: IMaterialPriceSearchRequest) => {
		setIsLoadingMaterialPrices(true);
		setErrorMessage(undefined);

		MaterialPriceService.searchMaterialPrices(searchRequest)
			.then((response) => {
				console.log('MaterialPriceService.searchMaterialPrices: SUCCESS', response);
				const results = response.data as IMaterialPriceSearchResult;
				
				results.records = MaterialPriceService.mapMaterialPrices(results.records);
				setSearchResults(results);
				setPreviousSearchRequest(searchRequest);
			})
			.catch((error) => {
				console.log('MaterialPriceService.searchMaterialPrices: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingMaterialPrices(false);
			});
	};

	const criteriaUpdated = async (searchRequest: IMaterialPriceSearchRequest) => {
		await searchMaterialPrices(searchRequest);
	};

	const handleExport = () => {
		setErrorMessage(undefined);
		setWorkingModalParams({ params: { content: "Exporting current material list..." } });
		setIsExporting(true);

		MaterialPriceService.exportMaterialPrices(previousSearchRequest)
			.then((response) => {
				console.log('MaterialPriceService.exportMaterialPrices: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.log('MaterialPriceService.exportMaterialPrices: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setWorkingModalParams(undefined);
				setIsExporting(false);
			});
	}

	const downloadFromByteArray = (file): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.content, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.filename;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}

	useEffect(() => {
		loadMaterialPriceData();
	}, [])

	return (
		<div className="hwi-material-price-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageMaterialPrices}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">Search Material Prices</div>
							<div className="actions">
								<Button variant="contained" color="primary" disabled={isLoadingMaterialPrices || isExporting || !previousSearchRequest || searchResults?.records?.length < 1} onClick={() => handleExport()}>Export Material Prices</Button>
								<NavLink to="/materialprice/add"><Button variant="outlined">New Material Price</Button></NavLink>
							</div>
						</div>
						<section className="search-options">
							<FormControl size="small">
								<Autocomplete
									blurOnSelect
									disablePortal
									disableClearable
									options={[{ name: 'All', value: '' }, ...materialOptions]}									
									renderInput={(params) => <TextField {...params} label="Material" />}
									renderOption={(props, item: ISelectOption) => ( <li {...props} key={item.value}>{item.name}</li> )}
									value={materialOptions.find(option => option.value === materialFilter) || null}
									onChange={(event, newValue) => {setMaterialFilter(newValue?.value ?? ''); }}
									getOptionLabel={(item) => item?.value == '' ? '' : item?.name ?? ''}
									isOptionEqualToValue={(item, value) => item?.value === value.value}
								/>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Customer</InputLabel>
								<Select
									value={customerFilter}
									label="Customer"
									onChange={(event) => setCustomerFilter(event.target.value) }
								>
									<MenuItem value="" key="-1">All</MenuItem>
									{customerOptions.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Vendor</InputLabel>
								<Select
									value={vendorFilter}
									label="Vendor"
									onChange={(event) => setVendorFilter(event.target.value)}
								>
									<MenuItem value="" key="-1">All</MenuItem>
									{vendorOptions.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={(event) => setStatusFilter(event.target.value)}
								>
									<MenuItem value="" key="-1">All</MenuItem>
									{statusOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
						</section>
					</section>
					<section className="results">
						{errorMessage &&
							<Alert className="alert-error"
								severity="error">
								{errorMessage}
							</Alert>
						}
						{!errorMessage &&
							<MaterialPriceTable data={searchResults} material={materialFilter} customer={customerFilter} vendor={vendorFilter} status={statusFilter} isAwaitingResultSet={isLoadingMaterialPrices} update={criteriaUpdated}></MaterialPriceTable>
						}							
					</section>
				</PermissionGate>
			</Container>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</div>
	);
};

export default MaterialPriceList;